// src/components/ModuleAdmin.js
import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Trash2, Edit, RefreshCw, 
  CheckCircle, AlertCircle, Loader
} from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';

const client = generateClient();

const ModuleAdmin = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    checkAdminStatusAndLoad();
  }, []);

  const checkAdminStatusAndLoad = async () => {
    try {
      setAuthChecking(true);
      const session = await fetchAuthSession();
      const groups = session.tokens.accessToken.payload['cognito:groups'] || [];
      
      if (!groups.includes('admin')) {
        navigate('/');
        return;
      }
      
      setIsAdmin(true);
      await loadModules();
    } catch (err) {
      console.error('Error checking admin status:', err);
      navigate('/');
    } finally {
      setAuthChecking(false);
    }
  };

  const loadModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await client.graphql({
        query: `
          query ListGameModules {
            listGameModules {
              items {
                id
                name
                description
                status
                creatorId
                createdAt
                updatedAt
                recommendedPlayers
                minLevel
                maxLevel
                maxPartySize
                difficulty
                estimatedPlayTime
                tags
              }
            }
          }
        `
      });

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      setModules(result.data.listGameModules.items);
    } catch (err) {
      console.error('Error loading modules:', err);
      setError('Failed to load game modules: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWintermere = async () => {
    try {
      setActionInProgress(true);
      setError(null);
      
      const session = await fetchAuthSession();
      const userId = session.tokens.accessToken.payload.sub;

      const result = await client.graphql({
        query: `
          mutation CreateGameModule($input: CreateGameModuleInput!) {
            createGameModule(input: $input) {
              id
              name
              description
              status
            }
          }
        `,
        variables: {
          input: {
            id: 'wintermere',
            name: 'Frost and Bones: The Ruins of Castle Wintermere',
            description: 'Embark on a chilling adventure in a frost-covered land.',
            scenes: JSON.stringify({
              intro: {
                id: "intro_01",
                title: "The Frozen Gates",
                description: "The bitter wind howls around you...",
                choices: []
              }
            }),
            recommendedPlayers: 1,
            minLevel: 1,
            maxLevel: 5,
            maxPartySize: 1,
            startingLevel: 1,
            difficulty: 'EASY',
            estimatedPlayTime: "2-3 hours",
            tags: ['Single Player', 'Story Rich', 'Fantasy'],
            version: "1.0.0",
            status: "PUBLISHED",
            creatorId: userId
          }
        }
      });

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      await loadModules();

    } catch (err) {
      console.error('Error creating Wintermere module:', err);
      setError('Failed to create module: ' + (err.message || 'Unknown error'));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) {
      return;
    }

    try {
      setActionInProgress(true);
      setError(null);

      const result = await client.graphql({
        query: `
          mutation DeleteGameModule($input: DeleteGameModuleInput!) {
            deleteGameModule(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: { id: moduleId }
        }
      });

      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      await loadModules();

    } catch (err) {
      console.error('Error deleting module:', err);
      setError('Failed to delete module: ' + (err.message || 'Unknown error'));
    } finally {
      setActionInProgress(false);
    }
  };

  if (authChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Game Module Administration</span>
            <div className="flex gap-2">
              <Button
                onClick={loadModules}
                disabled={loading || actionInProgress}
                variant="outline"
                size="sm"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleCreateWintermere}
                disabled={loading || actionInProgress}
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Wintermere
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {modules.map(module => (
                <Card key={module.id}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <h3 className="font-semibold">{module.name}</h3>
                      <p className="text-sm text-muted-foreground">{module.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          module.status === 'PUBLISHED' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {module.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Players: {module.recommendedPlayers} • 
                          Level: {module.minLevel}-{module.maxLevel}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteModule(module.id)}
                        disabled={actionInProgress}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {!loading && modules.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No game modules found. Create one to get started.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModuleAdmin;